import React, { useState } from 'react';
// FIX: Imported BulkUpdatePayload from the centralized types.ts file to resolve the module not found error.
import { SettingItem, Currency, BulkUpdatePayload } from '../types';
import { useSettings } from '../hooks/useSettings';
import { SearchableSelect } from './SearchableSelect';
import { DeleteIcon } from './icons/DeleteIcon';

interface BulkActionToolbarProps {
  selectedCount: number;
  onBulkDelete: () => void;
  onBulkUpdate: (updates: BulkUpdatePayload) => void;
  onClearSelection: () => void;
  totalFilteredCount: number;
  isSelectAllActive: boolean;
  onSelectAllFiltered: () => void;
  showSelectAllFilteredBanner: boolean;
}

const getSelectionText = (count: number) => {
    if (count === 1) return '1 položka vybrána';
    if (count > 1 && count < 5) return `${count} položky vybrány`;
    return `${count} položek vybráno`;
}

export const BulkActionToolbar: React.FC<BulkActionToolbarProps> = ({
  selectedCount,
  onBulkDelete,
  onBulkUpdate,
  onClearSelection,
  totalFilteredCount,
  isSelectAllActive,
  onSelectAllFiltered,
  showSelectAllFilteredBanner,
}) => {
    const settings = useSettings();
    
    const [category, setCategory] = useState<SettingItem | null>(null);
    const [tags, setTags] = useState<SettingItem[]>([]);
    const [paymentMethod, setPaymentMethod] = useState<SettingItem | null>(null);
    const [currency, setCurrency] = useState<Currency | null>(null);
    const [businessName, setBusinessName] = useState<string>('');
    const [receiptDate, setReceiptDate] = useState<string>('');

    // Sledujeme, zda se uživatel dotkl pole se štítky, abychom rozlišili
    // mezi "nechat původní" a "nastavit na prázdné".
    const [tagsTouched, setTagsTouched] = useState(false);

    const isAnyFieldFilled = () => {
        return category || tagsTouched || paymentMethod || currency || businessName.trim() !== '' || receiptDate !== '';
    }
    
    const handleApplyChanges = () => {
        const updates: BulkUpdatePayload = {};
        if (category) updates.category = category;
        // Štítky přidáme do aktualizace, pouze pokud s nimi uživatel interagoval.
        if (tagsTouched) updates.tags = tags;
        if (paymentMethod) updates.paymentMethod = paymentMethod;
        if (currency) updates.currency = currency;
        if (businessName.trim()) updates.businessName = businessName.trim();
        if (receiptDate) updates.receiptDate = receiptDate;
        
        if (Object.keys(updates).length > 0) {
            onBulkUpdate(updates);
        }
    };
    
    return (
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-4 space-y-4 animate-fade-in">
            <div className="flex justify-between items-center flex-wrap gap-2">
                <div>
                    <h3 className="font-semibold text-blue-800">{getSelectionText(selectedCount)}</h3>
                     {showSelectAllFilteredBanner && (
                        <div className="text-sm text-blue-700 mt-1">
                            <button onClick={onSelectAllFiltered} className="font-semibold underline hover:text-blue-900">
                                Vybrat všech {totalFilteredCount} položek odpovídajících filtru
                            </button>
                        </div>
                    )}
                    {isSelectAllActive && (
                        <div className="text-sm text-blue-700 mt-1">
                           Jsou vybrány všechny položky ({totalFilteredCount}).
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2">
                     <button
                        onClick={onClearSelection}
                        className="text-sm text-blue-600 hover:underline"
                    >
                        Zrušit výběr
                    </button>
                    <button
                        onClick={onBulkDelete}
                        className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 flex items-center gap-2"
                    >
                        <DeleteIcon className="w-4 h-4" />
                        Smazat vybrané
                    </button>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Kategorie</label>
                    <SearchableSelect
                        value={category}
                        onChange={(v) => setCategory(v as SettingItem)}
                        options={settings.categories}
                        placeholder="Změnit kategorii..."
                        onAddItem={(item) => settings.addItem('categories', item)}
                    />
                </div>
                 <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Štítky (přepíše)</label>
                    <SearchableSelect
                        isMulti
                        value={tags}
                        onChange={(v) => {
                            setTags(v as SettingItem[]);
                            setTagsTouched(true);
                        }}
                        options={settings.tags}
                        placeholder="Nastavit štítky..."
                        onAddItem={(item) => settings.addItem('tags', item)}
                    />
                </div>
                 <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Způsob platby</label>
                    <SearchableSelect
                        value={paymentMethod}
                        onChange={(v) => setPaymentMethod(v as SettingItem)}
                        options={settings.paymentMethods}
                        placeholder="Změnit platbu..."
                        onAddItem={(item) => settings.addItem('paymentMethods', item)}
                    />
                </div>
                 <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Měna</label>
                    <SearchableSelect
                        value={currency}
                        onChange={(v) => setCurrency(v as Currency)}
                        options={settings.currencies}
                        placeholder="Změnit měnu..."
                        onAddItem={(item) => settings.addItem('currencies', item)}
                    />
                </div>
                 <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Podnik</label>
                    <input
                        type="text"
                        value={businessName}
                        onChange={(e) => setBusinessName(e.target.value)}
                        placeholder="Změnit název podniku..."
                        className="p-1 border border-gray-300 rounded-md w-full text-sm"
                    />
                </div>
                <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Datum</label>
                    <input
                        type="date"
                        value={receiptDate}
                        onChange={(e) => setReceiptDate(e.target.value)}
                        className="p-1 border border-gray-300 rounded-md w-full text-sm"
                    />
                </div>
                <div className="lg:col-span-2 flex justify-end">
                    <button
                        onClick={handleApplyChanges}
                        disabled={!isAnyFieldFilled()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
                    >
                        Provést změny
                    </button>
                </div>
            </div>
        </div>
    );
};
